import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { blogPosts } from '../data/blogPosts';
import PublicLayout from '../components/layout/PublicLayout';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';

export default function Blog() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const categories = Array.from(new Set(blogPosts.map(post => post.category)));

  const filteredPosts = blogPosts
    .filter(post => {
      const q = searchQuery.toLowerCase();
      const matchesSearch = post.title.toLowerCase().includes(q) ||
                           post.excerpt.toLowerCase().includes(q) ||
                           post.keywords.some(kw => kw.toLowerCase().includes(q));
      const matchesCategory = !selectedCategory || post.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => b.publishDate.localeCompare(a.publishDate));

  const blogListSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Journal Clairify',
    description: 'Guides IA, BI et données pour PME françaises.',
    url: 'https://clairify.fr/blog',
    inLanguage: 'fr-FR',
    mainEntity: {
      '@type': 'ItemList',
      numberOfItems: blogPosts.length,
      itemListElement: [...blogPosts]
        .sort((a, b) => b.publishDate.localeCompare(a.publishDate))
        .map((post, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          url: `https://clairify.fr/blog/${post.slug}`,
          name: post.title,
        })),
    },
  };

  return (
    <PublicLayout>
      <SEOHead
        title="Journal Clairify — Guides par métier, audit, CA et insights"
        description={`${blogPosts.length} guides pour viser chaque type de boîte : commerces, artisans, cabinets, hôtels, industrie, e-commerce. Audit, insights, CA, patterns. IA métier France.`}
        keywords={['IA boulangerie', 'IA garage', 'IA hôtel', 'IA artisan', 'IA pharmacie', 'IA ESN', 'audit données entreprise', 'alternative Power BI', 'IA entreprise France']}
        canonicalUrl="https://clairify.fr/blog"
        structuredData={blogListSchema}
        markdownUrl="https://clairify.fr/blog.md"
      />

      <section className="pub-hero">
        <p className="home-kicker">Journal</p>
        <h1>Ce qu'il faut clarifier.</h1>
        <p>Dirigeants, DAF, commerciaux, artisans, commerces, cabinets, industrie. {blogPosts.length} guides, un métier après l’autre. Pour que vos données deviennent des décisions.</p>
      </section>

      <section className="pub-wrap">
        <input
          type="search"
          placeholder="Rechercher un article…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full max-w-xl mb-8 px-0 py-3 bg-transparent border-0 border-b border-theme text-theme-primary rounded-none"
        />

        <div className="pub-filters">
          <button
            onClick={() => setSelectedCategory(null)}
            className={!selectedCategory ? 'is-on' : ''}
          >
            Tous ({blogPosts.length})
          </button>
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={selectedCategory === category ? 'is-on' : ''}
            >
              {category}
            </button>
          ))}
        </div>

        {filteredPosts.length === 0 ? (
          <p className="text-theme-secondary">Aucun article pour « {searchQuery} »</p>
        ) : (
          <div className="pub-grid">
            {filteredPosts.map(post => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="pub-card">
                <span>{post.category}</span>
                <h3>{post.title}</h3>
                <p>{post.excerpt}</p>
                <time dateTime={post.publishDate}>
                  {new Date(post.publishDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  {' · '}{post.readTime} min
                </time>
              </Link>
            ))}
          </div>
        )}

        <div className="pub-cta">
          <p className="home-kicker">Studio</p>
          <h2>Prêt à voir ce que vos données cachent ?</h2>
          <p>Gratuit. Sans carte. 50 000 tokens.</p>
          <Link to="/login">
            <Button size="lg">Commencer</Button>
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
